/**
 * MietwagenKiosk — Tablet-optimierter Kiosk für Erstanmietung Unfallersatzfahrzeug
 * 
 * Design: Apple-like, clean, minimalistisch, no-scroll
 * Workflow: Willkommen → Checkliste → Persönliche Daten → Führerschein → Unfalldaten → Fahrzeugwahl → Vertrag → Unterschrift → Fertig
 * 
 * v1.0 — 07.04.2026
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import SignaturePad from 'signature_pad';
import {
  ArrowLeft, ArrowRight, User, Car, FileText,
  CheckCircle2, Shield, Clock, X, Camera,
  CreditCard, AlertTriangle, Sparkles, Phone,
  Mail, MapPin, Calendar, QrCode, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import PreisSchritt, { type PreisSchrittValues } from './mietwagen/wizard/PreisSchritt';

const KOOP_ROT = '#C41E3A';

type KioskStep = 'welcome' | 'checklist' | 'person' | 'fuehrerschein' | 'unfall' | 'fahrzeug' | 'konditionen' | 'vertrag' | 'complete';

interface MietKioskForm {
  anrede: string;
  vorname: string;
  nachname: string;
  strasse: string;
  plz: string;
  ort: string;
  telefon: string;
  email: string;
  geburtsdatum: string;
  fuehrerschein_nr: string;
  fuehrerschein_klasse: string;
  fuehrerschein_foto: string;
  unfall_datum: string;
  unfall_ort: string;
  gegner_name: string;
  gegner_kennzeichen: string;
  versicherung: string;
  schaden_nr: string;
  eigenes_kennzeichen: string;
  eigenes_fahrzeug: string;
  eigene_klasse: string;
  fahrzeug_id: string;
  fahrzeug_name: string;
  fahrzeug_kennzeichen: string;
  fahrzeug_kategorie: string;
  fahrzeug_klasse_id: number | null;
  selbst_betroffen: boolean;
  // Preiskonditionen (PreisSchritt)
  tagessatz: number;
  vollkasko: boolean;
  mietdauer_tage: number;
  reparaturzeit_tage: number;
  ist_totalschaden: boolean;
}

const EMPTY_FORM: MietKioskForm = {
  anrede: 'Herr',
  vorname: '',
  nachname: '',
  strasse: '',
  plz: '',
  ort: '',
  telefon: '',
  email: '',
  geburtsdatum: '',
  fuehrerschein_nr: '',
  fuehrerschein_klasse: 'B',
  fuehrerschein_foto: '',
  unfall_datum: new Date().toISOString().split('T')[0],
  unfall_ort: '',
  gegner_name: '',
  gegner_kennzeichen: '',
  versicherung: '',
  schaden_nr: '',
  eigenes_kennzeichen: '',
  eigenes_fahrzeug: '',
  eigene_klasse: '',
  fahrzeug_id: '',
  fahrzeug_name: '',
  fahrzeug_kennzeichen: '',
  fahrzeug_kategorie: '',
  fahrzeug_klasse_id: null,
  selbst_betroffen: true,
  tagessatz: 0,
  vollkasko: true,
  mietdauer_tage: 5,
  reparaturzeit_tage: 5,
  ist_totalschaden: false,
};

interface Fahrzeug {
  id: string;
  marke: string;
  modell: string;
  kennzeichen: string;
  kategorie: string;
  km_aktuell: number;
  mietwagen_klasse_id: number | null;
}

const STEPS: { id: KioskStep; label: string }[] = [
  { id: 'welcome', label: 'Start' },
  { id: 'checklist', label: 'Vorbereitung' },
  { id: 'person', label: 'Person' },
  { id: 'fuehrerschein', label: 'Führerschein' },
  { id: 'unfall', label: 'Unfall' },
  { id: 'fahrzeug', label: 'Fahrzeug' },
  { id: 'konditionen', label: 'Konditionen' },
  { id: 'vertrag', label: 'Vertrag' },
  { id: 'complete', label: 'Fertig' },
];

export default function MietwagenKiosk() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<KioskStep>('welcome');
  const [form, setForm] = useState<MietKioskForm>({ ...EMPTY_FORM });
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([]);
  const [checklistOk, setChecklistOk] = useState({ fuehrerschein: false, ausweis: false, unfalldaten: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vertragNr, setVertragNr] = useState('');

  // Signature
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const set = (key: keyof MietKioskForm, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const stepIndex = STEPS.findIndex(s => s.id === step);
  const progress = ((stepIndex) / (STEPS.length - 1)) * 100;

  // Fahrzeuge laden
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('fahrzeuge')
        .select('id, marke, modell, kennzeichen, kategorie, km_aktuell, mietwagen_klasse_id')
        .in('status', ['aktiv', 'verfuegbar'])
        .order('kennzeichen');
      setFahrzeuge(data || []);
    })();
  }, []);

  // Signature Pad
  useEffect(() => {
    if (step !== 'vertrag' || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
      minWidth: 0.5,
      maxWidth: 2.5,
    });
    pad.addEventListener('endStroke', () => setHasSignature(!pad.isEmpty()));
    signaturePadRef.current = pad;
    return () => { pad.off(); };
  }, [step]);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
  }, []);

  const goNext = () => {
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].id);
  };
  const goBack = () => {
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx > 0) setStep(STEPS[idx - 1].id);
  };

  // Submit
  const handleSubmit = async () => {
    if (!hasSignature || !agbAccepted) {
      toast.error('Bitte unterschreiben und AGB akzeptieren.');
      return;
    }
    setIsSubmitting(true);
    try {
      const nr = `LCS-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      setVertragNr(nr);

      await supabase.from('forderungsakten').insert({
        akten_nr: `MG-${nr}`,
        unfall_datum: form.unfall_datum || null,
        unfall_ort: form.unfall_ort || null,
        unfallgegner_name: form.gegner_name || null,
        unfallgegner_kennzeichen: form.gegner_kennzeichen || null,
        versicherung_gegner: form.versicherung || null,
        schaden_nummer: form.schaden_nr || null,
        fahrzeugklasse_beschaedigt: form.eigene_klasse || null,
        fahrzeugklasse_gemietet: form.fahrzeug_kategorie || null,
        mietvertrag_nr: nr,
        mietbeginn: new Date().toISOString().split('T')[0],
        tagessatz: form.tagessatz || 65,
        mietdauer_tage: form.mietdauer_tage,
        vollkasko: form.vollkasko,
        status: 'offen',
        mahnstufe: 'keine',
        anspruchsgrundlage: '§ 249 BGB - Naturalrestitution',
      });

      setStep('complete');
      toast.success('Mietvertrag erfolgreich erstellt!');
    } catch (err: any) {
      toast.error(`Fehler: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── RENDER ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      {/* Header */}
      {step !== 'welcome' && step !== 'complete' && (
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => step === 'checklist' ? navigate('/') : goBack()}
              className="p-2 rounded-xl bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-all">
              <ArrowLeft size={20} className="text-gray-500" />
            </button>
            <div className="text-center">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Erstanmietung</p>
              <p className="text-xs font-bold text-gray-700">{STEPS.find(s => s.id === step)?.label}</p>
            </div>
            <button onClick={() => navigate('/')}
              className="p-2 rounded-xl bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-all">
              <X size={20} className="text-gray-400" />
            </button>
          </div>
          {/* Progress */}
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${KOOP_ROT}, #E85D75)` }} />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-4">
        <div className="w-full max-w-lg">

          {/* WELCOME */}
          {step === 'welcome' && (
            <div className="text-center space-y-8">
              <div className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
                <Car size={36} className="text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Ihr Ersatzfahrzeug</h1>
                <p className="text-base text-gray-500 mt-3 max-w-sm mx-auto leading-relaxed">
                  Sie haben Anspruch auf ein Ersatzfahrzeug. Die Kosten werden von der <strong>gegnerischen Versicherung</strong> übernommen.
                </p>
              </div>
              <div className="space-y-3 text-left max-w-sm mx-auto">
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
                  <Clock size={18} className="text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-600">Dauer: ca. <strong>10 Minuten</strong></p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200">
                  <Shield size={18} className="text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-600">Ihre Daten sind <strong>sicher verschlüsselt</strong></p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <Sparkles size={18} className="text-emerald-500 flex-shrink-0" />
                  <p className="text-sm text-emerald-700"><strong>Keine Kosten</strong> für Sie — Abrechnung mit der Versicherung</p>
                </div>
              </div>
              <button onClick={goNext}
                className="w-full max-w-sm mx-auto flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-white font-bold text-lg shadow-lg hover:opacity-90 transition-all"
                style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
                Jetzt starten <ArrowRight size={20} />
              </button>
            </div>
          )}

          {/* CHECKLIST */}
          {step === 'checklist' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">Haben Sie alles dabei?</h2>
                <p className="text-sm text-gray-500 mt-2">Bitte prüfen Sie, ob Sie folgende Unterlagen griffbereit haben.</p>
              </div>
              <div className="space-y-3">
                {[
                  { key: 'fuehrerschein' as const, icon: CreditCard, label: 'Führerschein', desc: 'Zum Fotografieren oder Abtippen' },
                  { key: 'ausweis' as const, icon: User, label: 'Personalausweis', desc: 'Zur Identitätsprüfung' },
                  { key: 'unfalldaten' as const, icon: AlertTriangle, label: 'Unfalldaten', desc: 'Gegner, Versicherung, Schaden-Nr. (falls bekannt)' },
                ].map(item => (
                  <button key={item.key} onClick={() => setChecklistOk(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                      checklistOk[item.key] ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'
                    }`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                      checklistOk[item.key] ? 'bg-emerald-500' : 'bg-gray-100'
                    }`}>
                      {checklistOk[item.key] ? <CheckCircle2 size={24} className="text-white" /> : <item.icon size={24} className="text-gray-400" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <p className="text-xs text-blue-700 leading-relaxed">
                  <strong>Tipp:</strong> Nehmen Sie sich 10 Minuten Zeit und geben Sie die Daten so vollständig wie möglich ein. Unsere Mitarbeiter prüfen alles und stehen für Rückfragen bereit.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">Zurück</button>
                <button onClick={goNext}
                  className="flex-1 py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all"
                  style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
                  Weiter
                </button>
              </div>
            </div>
          )}

          {/* PERSON */}
          {step === 'person' && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 text-center">Persönliche Daten</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  {['Herr', 'Frau', 'Divers'].map(a => (
                    <button key={a} onClick={() => set('anrede', a)}
                      className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        form.anrede === a ? 'text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-600'
                      }`} style={form.anrede === a ? { background: KOOP_ROT } : {}}>
                      {a}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <KioskInput icon={User} label="Vorname *" value={form.vorname} onChange={v => set('vorname', v)} />
                  <KioskInput icon={User} label="Nachname *" value={form.nachname} onChange={v => set('nachname', v)} />
                </div>
                <KioskInput icon={MapPin} label="Straße & Hausnr." value={form.strasse} onChange={v => set('strasse', v)} />
                <div className="grid grid-cols-3 gap-3">
                  <KioskInput icon={MapPin} label="PLZ" value={form.plz} onChange={v => set('plz', v)} />
                  <div className="col-span-2"><KioskInput icon={MapPin} label="Ort" value={form.ort} onChange={v => set('ort', v)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <KioskInput icon={Phone} label="Telefon *" value={form.telefon} onChange={v => set('telefon', v)} type="tel" />
                  <KioskInput icon={Mail} label="E-Mail" value={form.email} onChange={v => set('email', v)} type="email" />
                </div>
                <KioskInput icon={Calendar} label="Geburtsdatum" value={form.geburtsdatum} onChange={v => set('geburtsdatum', v)} type="date" />
              </div>
              <NavButtons onBack={goBack} onNext={goNext} canNext={!!form.vorname && !!form.nachname && !!form.telefon} />
            </div>
          )}

          {/* FÜHRERSCHEIN */}
          {step === 'fuehrerschein' && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 text-center">Führerschein</h2>
              <div className="space-y-3">
                <KioskInput icon={CreditCard} label="Führerschein-Nr." value={form.fuehrerschein_nr} onChange={v => set('fuehrerschein_nr', v)} />
                <KioskInput icon={CreditCard} label="Klasse" value={form.fuehrerschein_klasse} onChange={v => set('fuehrerschein_klasse', v)} />
              </div>
              {/* Foto-Upload */}
              <div className="bg-white rounded-2xl border-2 border-dashed border-gray-300 p-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Camera size={28} className="text-gray-400" />
                </div>
                <p className="text-sm font-semibold text-gray-700">Führerschein fotografieren</p>
                <p className="text-xs text-gray-400 mt-1">Vorder- und Rückseite</p>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700 cursor-pointer transition-all">
                    <Upload size={16} />
                    Foto aufnehmen
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) {
                          set('fuehrerschein_foto', URL.createObjectURL(file));
                          toast.success('Foto gespeichert');
                        }
                      }} />
                  </label>
                </div>
                {form.fuehrerschein_foto && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-emerald-600">
                    <CheckCircle2 size={16} /> <span className="text-sm font-semibold">Foto gespeichert</span>
                  </div>
                )}
              </div>
              <NavButtons onBack={goBack} onNext={goNext} canNext={true} />
            </div>
          )}

          {/* UNFALL */}
          {step === 'unfall' && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900 text-center">Unfalldaten</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <KioskInput icon={Calendar} label="Unfalldatum" value={form.unfall_datum} onChange={v => set('unfall_datum', v)} type="date" />
                  <KioskInput icon={MapPin} label="Unfallort" value={form.unfall_ort} onChange={v => set('unfall_ort', v)} />
                </div>
                <KioskInput icon={User} label="Unfallgegner Name" value={form.gegner_name} onChange={v => set('gegner_name', v)} />
                <KioskInput icon={Car} label="Kennzeichen Unfallgegner" value={form.gegner_kennzeichen} onChange={v => set('gegner_kennzeichen', v)} />
                <KioskInput icon={Shield} label="Versicherung (Gegner)" value={form.versicherung} onChange={v => set('versicherung', v)} />
                <KioskInput icon={FileText} label="Schaden-Nr. (falls bekannt)" value={form.schaden_nr} onChange={v => set('schaden_nr', v)} />
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 mb-3">Ihr beschädigtes Fahrzeug</p>
                <div className="space-y-3">
                  <KioskInput icon={Car} label="Ihr Kennzeichen" value={form.eigenes_kennzeichen} onChange={v => set('eigenes_kennzeichen', v)} />
                  <KioskInput icon={Car} label="Marke & Modell" value={form.eigenes_fahrzeug} onChange={v => set('eigenes_fahrzeug', v)} />
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">Fahrzeugklasse</p>
                    <div className="grid grid-cols-3 gap-2">
                      {['kleinwagen', 'limousine', 'kombi', 'suv', 'van'].map(k => (
                        <button key={k} onClick={() => set('eigene_klasse', k)}
                          className={`py-2 rounded-xl text-xs font-semibold capitalize transition-all ${
                            form.eigene_klasse === k ? 'text-white' : 'bg-white border border-gray-200 text-gray-600'
                          }`} style={form.eigene_klasse === k ? { background: KOOP_ROT } : {}}>
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <NavButtons onBack={goBack} onNext={goNext} canNext={true} />
            </div>
          )}

          {/* FAHRZEUG */}
          {step === 'fahrzeug' && (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="text-xl font-bold text-gray-900">Ihr Ersatzfahrzeug</h2>
                <p className="text-xs text-gray-500 mt-1">Wählen Sie ein verfügbares Fahrzeug</p>
              </div>
              {form.eigene_klasse && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2">
                  <p className="text-xs text-blue-700">
                    Empfohlen: <strong className="capitalize">{form.eigene_klasse}</strong> (gleiche Klasse wie Ihr Fahrzeug)
                  </p>
                </div>
              )}
              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {fahrzeuge.map(fz => (
                  <button key={fz.id}
                    onClick={() => { set('fahrzeug_id', fz.id); set('fahrzeug_name', `${fz.marke} ${fz.modell}`); set('fahrzeug_kennzeichen', fz.kennzeichen); set('fahrzeug_kategorie', fz.kategorie); set('fahrzeug_klasse_id', fz.mietwagen_klasse_id); }}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                      form.fahrzeug_id === fz.id ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: form.fahrzeug_id === fz.id ? `${KOOP_ROT}20` : '#F3F4F6' }}>
                      <Car size={22} style={{ color: form.fahrzeug_id === fz.id ? KOOP_ROT : '#9CA3AF' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-gray-800">{fz.marke} {fz.modell}</p>
                      <p className="text-xs text-gray-500">{fz.kennzeichen} · <span className="capitalize">{fz.kategorie}</span></p>
                    </div>
                    {form.fahrzeug_id === fz.id && <CheckCircle2 size={22} className="text-red-500" />}
                    {fz.kategorie === form.eigene_klasse && form.fahrzeug_id !== fz.id && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">Empfohlen</span>
                    )}
                  </button>
                ))}
              </div>
              <NavButtons onBack={goBack} onNext={goNext} canNext={!!form.fahrzeug_id} />
            </div>
          )}

          {/* KONDITIONEN — Fracke-Tagessatz + Eigenersparnis + Vollkasko */}
          {step === 'konditionen' && (
            <PreisSchritt
              fahrzeugKlasseId={form.fahrzeug_klasse_id}
              geschaedigtenKlasseId={null}
              istSonderfahrzeug={
                form.fahrzeug_kategorie?.toLowerCase().includes('wohnmobil') ||
                form.fahrzeug_kategorie?.toLowerCase().includes('adria') ||
                false
              }
              values={{
                tagessatz: form.tagessatz,
                vollkasko: form.vollkasko,
                mietdauer_tage: form.mietdauer_tage,
                reparaturzeit_tage: form.reparaturzeit_tage,
                ist_totalschaden: form.ist_totalschaden,
              }}
              onChange={(v: PreisSchrittValues) => setForm(prev => ({ ...prev, ...v }))}
              onBack={goBack}
              onNext={goNext}
            />
          )}

          {/* VERTRAG & UNTERSCHRIFT */}
          {step === 'vertrag' && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 text-center">Mietvertrag unterschreiben</h2>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 text-[10px] text-gray-600 leading-relaxed max-h-[20vh] overflow-y-auto">
                <p className="font-bold text-gray-800 mb-1">Mietbedingungen (Kurzfassung)</p>
                <p>Der Mieter verpflichtet sich, das Fahrzeug pfleglich zu behandeln und nur im öffentlichen Straßenverkehr zu nutzen. Das Fahrzeug darf nur vom Mieter geführt werden. Bei Unfällen oder Schäden ist der Vermieter unverzüglich zu informieren. Die Abrechnung der Mietwagenkosten erfolgt gegenüber der gegnerischen Haftpflichtversicherung gemäß § 249 BGB.</p>
              </div>
              <button onClick={() => setAgbAccepted(!agbAccepted)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                  agbAccepted ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white'
                }`}>
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                  agbAccepted ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                }`}>
                  {agbAccepted && <CheckCircle2 size={12} className="text-white" />}
                </div>
                <span className="text-xs text-gray-700">Ich akzeptiere die Mietbedingungen und Datenschutzbestimmungen.</span>
              </button>
              {/* Signature */}
              <div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-bold text-gray-700">Unterschrift</span>
                  {hasSignature && (
                    <button onClick={clearSignature} className="text-[10px] text-red-500 font-semibold">Löschen</button>
                  )}
                </div>
                <div className="relative">
                  <canvas ref={canvasRef} className="w-full touch-none" style={{ height: '120px', cursor: 'crosshair' }} />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-sm text-gray-300 font-medium">Hier unterschreiben</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={goBack} className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600">Zurück</button>
                <button onClick={handleSubmit} disabled={!hasSignature || !agbAccepted || isSubmitting}
                  className="flex-1 py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-40"
                  style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
                  {isSubmitting ? 'Wird erstellt...' : 'Vertrag unterschreiben'}
                </button>
              </div>
            </div>
          )}

          {/* COMPLETE */}
          {step === 'complete' && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 rounded-3xl bg-emerald-500 mx-auto flex items-center justify-center shadow-lg">
                <CheckCircle2 size={40} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Alles erledigt!</h2>
                <p className="text-sm text-gray-500 mt-2">Ihr Mietvertrag wurde erfolgreich erstellt.</p>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-5 text-left space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Vertrag-Nr.</span>
                  <span className="font-bold text-gray-800">{vertragNr}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Fahrzeug</span>
                  <span className="font-bold text-gray-800">{form.fahrzeug_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Kennzeichen</span>
                  <span className="font-bold text-gray-800">{form.fahrzeug_kennzeichen}</span>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <p className="text-xs text-blue-700">
                  <strong>Nächster Schritt:</strong> Unser Team bereitet Ihr Fahrzeug vor. Sie werden benachrichtigt, sobald es zur Abholung bereit ist.
                </p>
              </div>
              <button onClick={() => { setForm({ ...EMPTY_FORM }); setStep('welcome'); setHasSignature(false); setAgbAccepted(false); }}
                className="w-full max-w-sm mx-auto py-4 rounded-2xl text-white font-bold text-base shadow-lg"
                style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
                Neuen Mietvertrag starten
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reusable Components ──

function KioskInput({ icon: Icon, label, value, onChange, type = 'text' }: {
  icon: any; label: string; value: string; onChange: (v: string) => void; type?: string;
}) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">{label}</p>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
          className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400 transition-all" />
      </div>
    </div>
  );
}

function NavButtons({ onBack, onNext, canNext, nextLabel = 'Weiter' }: {
  onBack: () => void; onNext: () => void; canNext: boolean; nextLabel?: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onBack} className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">
        Zurück
      </button>
      <button onClick={onNext} disabled={!canNext}
        className="flex-1 py-3.5 rounded-xl text-white font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-40"
        style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}>
        {nextLabel} <ArrowRight size={16} className="inline ml-1" />
      </button>
    </div>
  );
}
